import React from "react";
import styled from "styled-components";
import {
  CATEGORY_COLORS,
  COLOR_CATEGORIES,
  ITEMS_COLOR,
  OPACITY_LEFT,
} from "../../utils/constants";
import { useAppSelector } from "@store/hooks";
import { selectCurrentCategory } from "@store/mainReducer";
import { formatTotal } from "@utils/formatData";
import { TotalItem } from "@models/DataOverview";
import { useViewportMode } from "@utils/useMobileDetection";

const StyledDiv = styled.div`
  font-size: 16px;
  gap: 13px;
  .icone {
    width: 5px;
    height: 40px;
    display: inline-block;
    border-radius: 2px;
    position: relative;
    top: 4px;
  }
  .flux-bloc {
    text-align: justify;
    color: #004d66;
  }
  .value {
    font-weight: 800;
  }
`;

const CompactStyledDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 4px;

  .compact-item {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
  }
  .compact-icone {
    width: 4px;
    height: 24px;
    display: inline-block;
    border-radius: 2px;
    flex-shrink: 0;
  }
  .compact-text {
    color: #004d66;
    font-size: 11px;
    line-height: 14px;
  }
  .compact-value {
    font-weight: 800;
    color: #004d66;
    font-size: 11px;
  }

  @media (max-width: 500px) {
    .compact-text {
      font-size: 10px;
      line-height: 12px;
    }
    .compact-value {
      font-size: 10px;
    }
    .compact-icone {
      height: 20px;
    }
  }
`;

interface ItemsCapacityProps {
  totaux: TotalItem[];
}

const ItemsCapacity: React.FC<ItemsCapacityProps> = ({ totaux }) => {
  const currentCategory = useAppSelector(selectCurrentCategory);
  const { isDesktop, isTablet } = useViewportMode();

  const items = totaux.filter((item) => item.category !== "TOTAL");

  if (isTablet) {
    // Tablet: each item on its own line, compact vertical
    return (
      <CompactStyledDiv
        style={{ flexDirection: "column", alignItems: "center", gap: 0 }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 8,
          }}
        >
          {items.map((item, index) => {
            const color = COLOR_CATEGORIES[item.category];
            const opacity =
              CATEGORY_COLORS[color].toUpperCase() === currentCategory ||
              currentCategory === "TOTAL"
                ? 1
                : OPACITY_LEFT;
            return (
              <div key={index} className="compact-item">
                <span
                  className="compact-icone"
                  style={{ background: ITEMS_COLOR[color], opacity }}
                />
                <div>
                  <div className="compact-text" style={{ opacity }}>
                    {CATEGORY_COLORS[color]}
                  </div>
                  <div className="compact-value" style={{ opacity }}>
                    {formatTotal(item.total)} GW
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CompactStyledDiv>
    );
  }

  if (!isDesktop) {
    // Mobile: horizontal row
    return (
      <CompactStyledDiv>
        {items.map((item, index) => {
          const color = COLOR_CATEGORIES[item.category];
          const opacity =
            CATEGORY_COLORS[color].toUpperCase() === currentCategory ||
            currentCategory === "TOTAL"
              ? 1
              : OPACITY_LEFT;
          return (
            <div key={index} className="compact-item">
              <span
                className="compact-icone"
                style={{ background: ITEMS_COLOR[color], opacity }}
              />
              <div>
                <div className="compact-text" style={{ opacity }}>
                  {CATEGORY_COLORS[color]}
                </div>
                <div className="compact-value" style={{ opacity }}>
                  {formatTotal(item.total)} GW
                </div>
              </div>
            </div>
          );
        })}
      </CompactStyledDiv>
    );
  }

  // Desktop: vertical stack
  return (
    <StyledDiv className="flex flex-col justify-between">
      {items.map((item, index: number) => {
        const color = COLOR_CATEGORIES[item.category];
        const opacity =
          CATEGORY_COLORS[color].toUpperCase() === currentCategory ||
          currentCategory === "TOTAL"
            ? 1
            : OPACITY_LEFT;
        return (
          <div key={index}>
            <div className="row d-flex justify-content-center align-items-center">
              <div className="col-1 text-start">
                <span
                  className="icone"
                  style={{ background: ITEMS_COLOR[color], opacity }}
                ></span>
              </div>
              <div className="col-4 flux-bloc">
                <span style={{ opacity }}>{`${CATEGORY_COLORS[color]}`}</span>
                <br />
                <span className="value" style={{ opacity }}>
                  {`${formatTotal(item.total)} GW`}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </StyledDiv>
  );
};

export default ItemsCapacity;
